import { forwardRef, useContext, useEffect, useRef, useImperativeHandle } from "react";
import { GoogleMapsContext } from "@vis.gl/react-google-maps";
import type { Ref } from "react";

type PolylineEventProps = {
	onClick?: (e: google.maps.MapMouseEvent) => void;
	onMouseOver?: (e: google.maps.MapMouseEvent) => void;
	onMouseOut?: (e: google.maps.MapMouseEvent) => void;
};

export type PolylineProps = google.maps.PolylineOptions & PolylineEventProps;

export type PolylineRef = Ref<google.maps.Polyline | null>;

function usePolyline(props: PolylineProps) {
	const { onClick, onMouseOver, onMouseOut, ...polylineOptions } = props;
	const callbacks = useRef<Record<string, (e: unknown) => void>>({});
	Object.assign(callbacks.current, { onClick, onMouseOver, onMouseOut });

	const polylineRef = useRef<google.maps.Polyline | null>(null);
	if (!polylineRef.current) {
		polylineRef.current = new google.maps.Polyline();
	}
	const polyline = polylineRef.current;

	useEffect(() => {
		polyline.setOptions(polylineOptions);
	}, [polyline, polylineOptions]);

	const map = useContext(GoogleMapsContext)?.map;

	useEffect(() => {
		if (!map) {
			if (map === undefined) console.error("<Polyline> has to be inside a Map component.");
			return;
		}

		polyline.setMap(map);

		return () => {
			polyline.setMap(null);
		};
	}, [map]);

	useEffect(() => {
		if (!polyline) return;

		const gme = google.maps.event;
		[
			["click", "onClick"],
			["mouseover", "onMouseOver"],
			["mouseout", "onMouseOut"],
		].forEach(([eventName, eventCallback]) => {
			gme.addListener(polyline, eventName, (e: google.maps.MapMouseEvent) => {
				const callback = callbacks.current[eventCallback];
				if (callback) callback(e);
			});
		});

		return () => {
			gme.clearInstanceListeners(polyline);
		};
	}, [polyline]);

	return polyline;
}

/**
 * Component to render a polyline on a map
 */
export const Polyline = forwardRef((props: PolylineProps, ref: PolylineRef) => {
	const polyline = usePolyline(props);

	useImperativeHandle(ref, () => polyline, []);

	return null;
});

Polyline.displayName = "Polyline";
